import React, { useState } from 'react';
import { Box, Paper, Typography, TextField, Button, Grid, MenuItem, Alert, Divider, FormControl, InputLabel, Select } from '@mui/material';
import api from '../../api';

const LeaveAllocationAssign = ({ empId, departments, showMsg, setActiveTab }) => {
  const [allocForm, setAllocForm] = useState({ 
    leave_type: 'CL', 
    days: 1, 
    expiry_date: '', 
    reason: '', 
    assignment_type: 'ALL', 
    target_id: '', 
    condition_joined_before: '', 
    condition_worked_days: '' 
  });

  const handleAllocSubmit = async (e) => {
    e.preventDefault();
    const payload = { ...allocForm, created_by: empId };
    if (!payload.target_id) payload.target_id = null;
    if (!payload.condition_joined_before) payload.condition_joined_before = null;
    if (!payload.condition_worked_days) payload.condition_worked_days = null;
    
    try {
      await api.post('/leave/allocations', payload);
      showMsg("Allocation created successfully!");
      setAllocForm({ leave_type: 'CL', days: 1, expiry_date: '', reason: '', assignment_type: 'ALL', target_id: '', condition_joined_before: '', condition_worked_days: '' });
      setActiveTab('alloc_history');
    } catch (err) { 
      showMsg(err.response?.data?.detail || "Failed", "error"); 
    }
  };

  return (
    <Box sx={{ maxWidth: '100%' }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" color="primary" sx={{ 
          fontWeight: 700, 
          fontSize: '1.1rem', 
          textTransform: 'uppercase', 
          letterSpacing: '0.5px',
          textAlign: 'left'
        }}>
          Assign Master Leave
        </Typography>
        <Typography variant="body2" color="textSecondary" sx={{ 
          mt: 0.5, 
          fontSize: '0.8rem',
          textAlign: 'left'
        }}>
          Allocate leave days to employees, departments, or the entire organization
        </Typography>
      </Box>

      <Box component="form" onSubmit={handleAllocSubmit}>
        
        {/* ===== LEAVE DETAILS SECTION ===== */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" color="textSecondary" sx={{ 
            fontWeight: 600, 
            fontSize: '0.75rem', 
            textTransform: 'uppercase', 
            letterSpacing: '0.5px',
            mb: 2,
            textAlign: 'left',
            borderBottom: '1px solid',
            borderColor: 'divider',
            pb: 1
          }}>
            Leave Details
          </Typography>

          <Grid container spacing={2} alignItems="center">
            {/* Leave Type - Fixed width with proper label */}
            <Grid item xs={12} sm={10}>
              <FormControl fullWidth size="small">
                <InputLabel>Leave Type</InputLabel>
                <Select
                  value={allocForm.leave_type}
                  label="Leave Type"
                  onChange={e => setAllocForm({...allocForm, leave_type: e.target.value})}
                  sx={{ bgcolor: '#ffffff' }}
                >
                  {['CL', 'ML', 'AL', 'EL', 'NH', 'WO'].map(lt => (
                    <MenuItem key={lt} value={lt}>{lt}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Days - Properly small field */}
            <Grid item xs={12} sm={1.5}>
              <TextField 
                fullWidth 
                size="small" 
                type="number" 
                label="Days" 
                required 
                inputProps={{ step: "0.5", min: 0.5, style: { textAlign: 'center' } }}
                value={allocForm.days} 
                onChange={e => setAllocForm({...allocForm, days: parseFloat(e.target.value)})}
                sx={{ 
                  bgcolor: '#ffffff',
                  '& .MuiInputBase-input': {
                    textAlign: 'center',
                    padding: '8.5px 8px'
                  }
                }}
              />
            </Grid>

            {/* Expiry Date */}
            <Grid item xs={12} sm={3}>
              <TextField 
                fullWidth 
                size="small" 
                type="date" 
                label="Expiry Date" 
                required 
                InputLabelProps={{ shrink: true }} 
                value={allocForm.expiry_date} 
                onChange={e => setAllocForm({...allocForm, expiry_date: e.target.value})}
                sx={{ bgcolor: '#ffffff' }}
              />
            </Grid>

            {/* Reason */}
            <Grid item xs={12} sm={4.5}>
              <TextField 
                fullWidth 
                size="small" 
                label="Reason" 
                placeholder="Max 15 chars"
                inputProps={{ maxLength: 15 }} 
                required 
                value={allocForm.reason} 
                onChange={e => setAllocForm({...allocForm, reason: e.target.value})}
                sx={{ bgcolor: '#ffffff' }}
              />
            </Grid>
          </Grid>
        </Box>

        {/* ===== TARGET GROUP SECTION ===== */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" color="textSecondary" sx={{ 
            fontWeight: 600, 
            fontSize: '0.75rem', 
            textTransform: 'uppercase', 
            letterSpacing: '0.5px',
            mb: 2,
            textAlign: 'left',
            borderBottom: '1px solid',
            borderColor: 'divider',
            pb: 1
          }}>
            Target Group
          </Typography>

          <Grid container spacing={2}>
            {/* Assignment Group - Full width dropdown */}
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel>Assignment Group</InputLabel>
                <Select
                  value={allocForm.assignment_type}
                  label="Assignment Group"
                  onChange={e => setAllocForm({...allocForm, assignment_type: e.target.value})}
                  sx={{ bgcolor: '#ffffff' }}
                >
                  <MenuItem value="ALL">Assign to ALL Active Employees</MenuItem>
                  <MenuItem value="DEPARTMENT">Specific Department</MenuItem>
                  <MenuItem value="EMPLOYEE">Specific Employee</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Conditional: Department Selection - Fixed width with proper label */}
            {allocForm.assignment_type === 'DEPARTMENT' && (
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Select Department</InputLabel>
                  <Select
                    value={allocForm.target_id}
                    label="Select Department"
                    onChange={e => setAllocForm({...allocForm, target_id: e.target.value})}
                    sx={{ bgcolor: '#ffffff' }}
                  >
                    {departments.map(d => (
                      <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}

            {/* Conditional: Employee Selection */}
            {allocForm.assignment_type === 'EMPLOYEE' && (
              <Grid item xs={12} sm={4}>
                <TextField 
                  fullWidth 
                  size="small" 
                  label="Employee ID" 
                  required 
                  value={allocForm.target_id} 
                  onChange={e => setAllocForm({...allocForm, target_id: e.target.value})}
                  sx={{ bgcolor: '#ffffff' }}
                />
              </Grid>
            )}
          </Grid>
        </Box>

        {/* ===== OPTIONAL FILTERS SECTION ===== */}
        {allocForm.assignment_type !== 'EMPLOYEE' && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" color="textSecondary" sx={{ 
              fontWeight: 600, 
              fontSize: '0.75rem', 
              textTransform: 'uppercase', 
              letterSpacing: '0.5px',
              mb: 1,
              textAlign: 'left',
              borderBottom: '1px solid',
              borderColor: 'divider',
              pb: 1
            }}>
              Optional Filters (Apply One or Both Conditions)
            </Typography>

            <Alert severity="info" sx={{ 
              mb: 2, 
              py: 0.5, 
              fontSize: '0.75rem',
              textAlign: 'left'
            }}>
              <strong>Note:</strong> Only employees matching these conditions will receive the allocation
            </Alert>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField 
                  fullWidth 
                  size="small" 
                  type="date" 
                  label="Joined On or Before" 
                  InputLabelProps={{ shrink: true }} 
                  value={allocForm.condition_joined_before} 
                  disabled={Boolean(allocForm.condition_worked_days)} 
                  onChange={e => setAllocForm({...allocForm, condition_joined_before: e.target.value})}
                  sx={{ bgcolor: '#ffffff' }}
                  helperText={allocForm.condition_worked_days ? "? Disabled (Worked Days filter is active)" : "Filter by join date"}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField 
                  fullWidth 
                  size="small" 
                  type="number" 
                  label="OR Worked Minimum Days" 
                  value={allocForm.condition_worked_days} 
                  disabled={Boolean(allocForm.condition_joined_before)} 
                  onChange={e => setAllocForm({...allocForm, condition_worked_days: e.target.value})}
                  sx={{ bgcolor: '#ffffff' }}
                  helperText={allocForm.condition_joined_before ? "? Disabled (Join Date filter is active)" : "Minimum days worked"}
                />
              </Grid>
            </Grid>
          </Box>
        )}

        {/* ===== ACTION BUTTONS ===== */}
        <Box sx={{ 
          display: 'flex', 
          gap: 2, 
          justifyContent: 'flex-start',
          mt: 2,
          pt: 2,
          borderTop: '1px solid',
          borderColor: 'divider'
        }}>
          <Button 
            variant="outlined" 
            color="secondary"
            onClick={() => {
              setAllocForm({ 
                leave_type: 'CL', 
                days: 1, 
                expiry_date: '', 
                reason: '', 
                assignment_type: 'ALL', 
                target_id: '', 
                condition_joined_before: '', 
                condition_worked_days: '' 
              });
            }}
            sx={{ 
              borderRadius: 2,
              px: 3,
              py: 0.8,
              fontSize: '0.8rem',
              textTransform: 'none',
              fontWeight: 500,
              minWidth: '120px'
            }}
          >
            Reset Form
          </Button>
          <Button 
            type="submit" 
            variant="contained"
            sx={{ 
              borderRadius: 2,
              px: 4,
              py: 0.8,
              fontSize: '0.8rem',
              textTransform: 'none',
              fontWeight: 600,
              minWidth: '160px',
              boxShadow: '0 2px 8px rgba(15, 108, 189, 0.3)',
              '&:hover': {
                boxShadow: '0 4px 12px rgba(15, 108, 189, 0.4)',
              }
            }}
          >
            Submit Allocation
          </Button>
        </Box>

      </Box>
    </Box>
  );
};

export default LeaveAllocationAssign;